import { useIntl } from 'react-intl';

import type { ScanProgress } from '@/hooks/use-scan-progress';
import type { TranslationKeys } from '@/lib/translations/types';
import { cn } from '@/lib/utils';
import type { FileRouteTypes } from '@/routeTree.gen';

import {
  IntegrationButton,
  ScanningStatusText,
  WarningsTooltip,
} from './IntegrationHelpers';

interface ColorOptions {
  connected: boolean;
  available: boolean;
  pending: boolean;
  isScanning: boolean;
  isSyncing: boolean;
  donePercentage?: number;
}

const getStatusColor = (options: ColorOptions) => {
  if (options.pending) {
    return 'bg-gray-200 border-gray-200 rounded-sm animate-pulse dark:bg-background dark:border-background';
  }
  if (options.isScanning) {
    return 'border-blue-500 text-black dark:text-white bg-blue-50 dark:bg-blue-950';
  }
  if (options.isSyncing) {
    return 'border-amber-500 text-black dark:text-white bg-amber-50 dark:bg-amber-950 animate-slow-pulse';
  }
  if (!options.available) {
    return 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500';
  }

  return null;
};

const getConnectionColor = (connected: boolean, donePercentage?: number) => {
  if (!connected) {
    return 'border-slate-400 text-black dark:text-white bg-slate-50 dark:bg-slate-900';
  }
  if (donePercentage === 0) {
    return 'border-red-500 text-black dark:text-white bg-red-50 dark:bg-red-950';
  }
  if (donePercentage !== undefined && donePercentage < 100) {
    return 'border-amber-500 text-black dark:text-white bg-amber-50 dark:bg-amber-950';
  }

  return 'border-green-500 text-black dark:text-white bg-green-50 dark:bg-green-950';
};

const getColor = (options: ColorOptions) => {
  return (
    getStatusColor(options) ??
    getConnectionColor(options.connected, options.donePercentage)
  );
};

interface TextOptions {
  connected: boolean;
  available: boolean;
  pending: boolean;
  donePercentage?: number;
  isScanning: boolean;
  scanPercentage?: number;
  isSyncing: boolean;
}

const getStatusText = (options: TextOptions): string | null => {
  const { pending, isScanning, scanPercentage, isSyncing } = options;
  if (pending) {
    return '...';
  }
  if (isScanning && scanPercentage !== undefined) {
    return `Scanning... (${scanPercentage}%)`;
  }
  if (isSyncing) {
    return 'Syncing';
  }

  return null;
};

const getText = (
  options: TextOptions,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
) => {
  const { connected, available, donePercentage } = options;

  const statusText = getStatusText(options);
  if (statusText !== null) {
    return statusText;
  }

  if (!available) {
    return 'Coming soon';
  }

  if (connected && donePercentage !== undefined) {
    return `${formatMessage({ id: 'global.status.connected' })} (${donePercentage}%)`;
  }

  return connected
    ? formatMessage({ id: 'global.status.connected' })
    : formatMessage({ id: 'global.status.readyToConnect' });
};

interface Props {
  logoUrl: string;
  connected: boolean;
  title: TranslationKeys;
  available: boolean;
  onClick?: () => void;
  to?: FileRouteTypes['to'];
  pending?: boolean;
  warnings?: TranslationKeys[];
  donePercentage?: number;
  scanProgress?: ScanProgress;
  isSyncing?: boolean;
}

// eslint-disable-next-line complexity
export const Integration = ({
  title,
  logoUrl,
  connected,
  available,
  to,
  onClick = () => undefined,
  pending = false,
  warnings = [],
  donePercentage,
  scanProgress,
  isSyncing = false,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();
  const isScanning = scanProgress !== undefined;
  const scanPercentage = scanProgress
    ? Math.floor(
        (100 * scanProgress.servicesScannedCount) /
          Math.max(scanProgress.totalServicesToScan, 1),
      )
    : undefined;

  return (
    <div className="border border-gray-400 p-2 flex flex-col items-center rounded-sm gap-2">
      <div className="dark:bg-gray-200 rounded-sm p-2">
        <img className="w-[40px] h-[40px]" src={logoUrl} alt={title} />
      </div>
      <div
        className={cn(
          'text-center border w-full lg:text-sm p-2 text-xs text-ellipsis truncate relative transition-all duration-500 ease-in-out overflow-hidden',
          getColor({
            connected,
            available,
            pending,
            isScanning,
            isSyncing,
            donePercentage,
          }),
        )}
      >
        {isScanning && (
          <div
            className="absolute inset-0 bg-blue-200 dark:bg-blue-800 transition-all duration-300 ease-out"
            style={{ width: `${scanPercentage}%` }}
          />
        )}
        <p className="text-md relative">
          {isScanning ? (
            <ScanningStatusText scanPercentage={scanPercentage ?? 0} />
          ) : (
            getText(
              {
                connected,
                available,
                pending,
                donePercentage,
                isScanning,
                scanPercentage,
                isSyncing,
              },
              formatMessage,
            )
          )}
          <WarningsTooltip warnings={warnings} connected={connected} />
        </p>
      </div>
      <IntegrationButton
        to={to}
        available={available}
        onClick={onClick}
        title={title}
        formatMessage={formatMessage}
      />
    </div>
  );
};
